/**
 * <switch-table-images> — logic layer for the Table Images block
 * (blocks/switch-table-images.liquid, BLK-005 / Decision #31).
 *
 * Responsibilities (FRONTEND-STACK layering):
 * - Build the mobile selector UI (pills / carousel / dropdown) from the
 *   rendered child images' data-option-name attributes (child theme
 *   blocks are isolated — the parent can't read their settings)
 * - Preload images when the block nears the viewport (IntersectionObserver)
 * - Handle selector interaction and delegate ALL swap motion to
 *   window.SwitchMotion.tableImageSwap (GSAP layer; resolved lazily at
 *   interaction time — absent or reduced-motion → instant swap)
 * - Theme Editor: selecting a child option block jumps to its image
 */

class SwitchTableImages extends HTMLElement {
  #images = [];
  #currentIndex = 0;
  #isAnimating = false;
  #preloaded = false;
  #io = null;
  #onBlockSelect = null;

  connectedCallback() {
    this.#images = Array.from(this.querySelectorAll('.switch-ti__image'));
    if (!this.#images.length) return;

    const wrapper = this.querySelector('[data-ti-images]');
    if (wrapper) wrapper.classList.add('is-initialized');
    this.#images[0].classList.add('active');

    this.#buildSelector();
    this.#setupPreload();

    this.#onBlockSelect = (event) => {
      const img = event.target && event.target.closest && event.target.closest('.switch-ti__image');
      if (!img || !this.contains(img)) return;
      const index = this.#images.indexOf(img);
      if (index >= 0) this.#switchTo(index, null, true);
    };
    document.addEventListener('shopify:block:select', this.#onBlockSelect);
  }

  disconnectedCallback() {
    if (this.#io) this.#io.disconnect();
    if (this.#onBlockSelect) {
      document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    }
  }

  #optionNames() {
    return this.#images.map((img) => img.dataset.optionName || '');
  }

  /* ---------------- selector construction ---------------- */

  #buildSelector() {
    const style = this.dataset.selectorStyle;
    if (style === 'pills') this.#buildPills();
    if (style === 'carousel') this.#buildCarousel();
    if (style === 'dropdown') this.#buildDropdown();
  }

  #buildPills() {
    const container = this.querySelector('[data-ti-pills]');
    if (!container) return;
    container.replaceChildren();
    this.#optionNames().forEach((name, index) => {
      if (!name) return;
      const pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'switch-ti__pill' + (index === 0 ? ' active' : '');
      pill.textContent = name;
      pill.setAttribute('aria-pressed', index === 0 ? 'true' : 'false');
      pill.addEventListener('click', () => {
        this.#switchTo(index);
        container.querySelectorAll('.switch-ti__pill').forEach((p, i) => {
          p.classList.toggle('active', i === index);
          p.setAttribute('aria-pressed', i === index ? 'true' : 'false');
        });
      });
      container.append(pill);
    });
  }

  #buildCarousel() {
    const label = this.querySelector('[data-ti-label]');
    const prevBtn = this.querySelector('[data-ti-prev]');
    const nextBtn = this.querySelector('[data-ti-next]');
    const names = this.#optionNames();

    const update = () => {
      if (label) label.textContent = names[this.#currentIndex] || '';
    };
    update();

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        const next = this.#currentIndex > 0 ? this.#currentIndex - 1 : this.#images.length - 1;
        this.#switchTo(next, 'prev').then(update);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        const next = this.#currentIndex < this.#images.length - 1 ? this.#currentIndex + 1 : 0;
        this.#switchTo(next, 'next').then(update);
      });
    }
  }

  #buildDropdown() {
    const dropdown = this.querySelector('[data-ti-dropdown]');
    if (!dropdown) return;
    dropdown.replaceChildren();
    this.#optionNames().forEach((name, index) => {
      if (!name) return;
      const option = document.createElement('option');
      option.value = String(index);
      option.textContent = name;
      if (index === 0) option.selected = true;
      dropdown.append(option);
    });
    dropdown.addEventListener('change', () => {
      this.#switchTo(parseInt(dropdown.value, 10));
    });
  }

  /* ---------------- preload ---------------- */

  #preloadAll() {
    if (this.#preloaded) return;
    this.#images.forEach((img) => {
      if (img.src) new Image().src = img.src;
    });
    this.#preloaded = true;
  }

  #setupPreload() {
    if (!('IntersectionObserver' in window)) {
      this.#preloadAll();
      return;
    }
    this.#io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.#preloadAll();
            this.#io.disconnect();
          }
        });
      },
      { rootMargin: '200px' }
    );
    this.#io.observe(this);
  }

  /* ---------------- image switching ---------------- */

  /**
   * @param {number} newIndex
   * @param {('next'|'prev')} [direction]
   * @param {boolean} [instant] — skip motion (Theme Editor jumps)
   * @returns {Promise<void>}
   */
  #switchTo(newIndex, direction, instant = false) {
    if (newIndex < 0 || newIndex >= this.#images.length) return Promise.resolve();
    if (this.#isAnimating || newIndex === this.#currentIndex) return Promise.resolve();

    const wrapper = this.querySelector('[data-ti-images]');
    const fromImg = this.#images[this.#currentIndex];
    const toImg = this.#images[newIndex];
    if (!wrapper || !fromImg || !toImg) return Promise.resolve();

    if (!direction) direction = newIndex > this.#currentIndex ? 'next' : 'prev';
    this.#currentIndex = newIndex;

    const finish = () => {
      fromImg.classList.remove('active');
      toImg.classList.add('active');
      this.#isAnimating = false;
    };

    const effect = this.dataset.transitionEffect;
    // Motion lives in the GSAP layer; resolved lazily so module-eval
    // order vs the deferred GSAP bundle never matters.
    const motion = window.SwitchMotion && window.SwitchMotion.tableImageSwap;
    if (instant || effect === 'none' || !motion) {
      finish();
      return Promise.resolve();
    }

    this.#isAnimating = true;
    return motion(wrapper, fromImg, toImg, {
      effect: effect,
      duration: parseInt(this.dataset.transitionDuration, 10) || 300,
      timing: this.dataset.transitionTiming,
      direction: direction,
    }).then(finish);
  }
}

if (!customElements.get('switch-table-images')) {
  customElements.define('switch-table-images', SwitchTableImages);
}
